<template>
	<div id="calc-1" class="calc-flex-col card calc-style">
		<div class="card-header">
			<h4 class="calc-text-center">
				Flow Velocity & Shear Rate Calculator
			</h4>
		</div>
		<div class="card-body">
			<div v-if="errored">
				<p>
					We're sorry, it appears we're unable to retrieve this
					information at the moment. Please try back later.
				</p>
			</div>
			<form v-else ref="loadingContainer" class="calc-form vld-parent">
				<loading
					:active="isLoading"
					:is-full-page="loaderSettings.fullPage"
					:height="loaderSettings.height"
					:width="loaderSettings.width"
					:color="loaderSettings.color"
					:loader="loaderSettings.loader"
					:blur="loaderSettings.blur"
				/>
				<div class="calc-flex-col calc-pb-2">
					<label for=""
						>Product Number
						<small
							><i
								>Continue typing to filter part numbers.</i
							></small
						></label
					>
					<div class="calc-flex-row">
						<SearchAutocomplete
							:key="componentKey"
							v-model="form.partNumber"
							:product-data="productData"
							@report-part-number="getPartNumber"
						/>

						<span
							v-if="v$.form.searchInput.$error"
							class="error calc-px-1"
							>{{ v$.form.searchInput.$errors[0].$message }}</span
						>
					</div>
				</div>

				<div class="calc-grid-col-2 calc-pb-2">
					<div class="calc-flex-col">
						<label for="">Fiber Diameter (mm)</label>
						<input
							v-model="form.fibDiameterMM"
							type="text"
							class="disabled"
							disabled
							@keyup.enter="submitForm"
						/>
						<span
							v-if="v$.form.fibDiameterMM.$error"
							class="error calc-px-1"
							>{{
								v$.form.fibDiameterMM.$errors[0].$message
							}}</span
						>
					</div>
					<div class="calc-flex-col">
						<label for="">Fiber Count </label>
						<input
							v-model="form.fiberCount"
							type="text"
							class="disabled"
							disabled
							@keyup.enter="submitForm"
						/>
						<span
							v-if="v$.form.fiberCount.$error"
							class="error calc-px-1"
							>{{ v$.form.fiberCount.$errors[0].$message }}</span
						>
					</div>
				</div>

				<div class="calc-grid-col-2 calc-pb-2">
					<div class="calc-flex-col">
						<label for="">Fiber Length (cm)</label>
						<input
							v-model="form.fibLength"
							type="text"
							class="disabled"
							disabled
							@keyup.enter="submitForm"
						/>
						<span
							v-if="v$.form.fibLength.$error"
							class="error calc-px-1"
							>{{ v$.form.fibLength.$errors[0].$message }}</span
						>
					</div>

					<div class="calc-flex-col">
						<label for=""
							>Flow Rate (L/min)
							<span class="calc-required-field"
								>Required</span
							></label
						>
						<input
							v-model="form.flowRate"
							type="text"
							@keyup.enter="submitForm"
						/>
						<span
							v-if="v$.form.flowRate.$error"
							class="error calc-px-1"
							>{{ v$.form.flowRate.$errors[0].$message }}</span
						>
					</div>
				</div>

				<div class="calc-grid-col-2 calc-pb-2">
					<div class="calc-flex-col">
						<label for=""
							>Fluid Viscosity (cP)
							<span class="calc-required-field"
								>Required</span
							></label
						>
						<input
							v-model="form.fluidViscosity"
							type="text"
							@keyup.enter="submitForm"
						/>
						<span
							v-if="v$.form.fluidViscosity.$error"
							class="error calc-px-1"
							>{{
								v$.form.fluidViscosity.$errors[0].$message
							}}</span
						>
					</div>

					<div class="calc-flex-col">
						<label for=""
							>Fluid Density (kg/L, g/mL)
							<span class="calc-required-field"
								>Required</span
							></label
						>
						<input
							v-model="form.fluidDensity"
							type="text"
							@keyup.enter="submitForm"
						/>
						<span
							v-if="v$.form.fluidDensity.$error"
							class="error calc-px-1"
							>{{
								v$.form.fluidDensity.$errors[0].$message
							}}</span
						>
					</div>
				</div>

				<div
					class="calc-flex calc-flex-sm-col--rev calc-flex-justify-center calc-flex-sm-align-items"
				>
					<button
						type="button"
						class="reset-btn btns calc-my-2"
						@click="resetForm"
					>
						RESET
					</button>
					<button
						type="button"
						class="submit-btn btns calc-my-2"
						@click="submitForm"
					>
						CALCULATE
					</button>
				</div>
			</form>
		</div>

		<div class="calc-output">
			<h4>Calculation Results</h4>
			<table class="border-collapse table-auto w-full calc-table-striped">
				<thead>
					<tr class="calc-bg-gray text-white">
						<th class="calc-p-4 calc-pl-8 calc-pb-3 text-left">
							Properties
						</th>
						<th class="calc-p-4 calc-pb-3 text-left">Values</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td class="border-b calc-p-2 calc-pl-8">
							<p>Flow Rate Per Fiber (L/min/fiber)</p>
						</td>
						<td class="border-b calc-p-2">
							<p>{{ flowRatePerFiberLMin }}</p>
						</td>
					</tr>
					<tr>
						<td class="border-b calc-p-2 calc-pl-8">
							<p>Flow Velocity Per Fiber (m/s)</p>
						</td>
						<td class="border-b calc-p-2">
							<p>
								{{ velocityPerChannel }}
							</p>
						</td>
					</tr>
					<tr>
						<td class="border-b calc-p-2 calc-pl-8">
							<p>Holdup Volume (L)</p>
						</td>
						<td class="border-b calc-p-2">
							<p>
								{{ volume }}
							</p>
						</td>
					</tr>
					<tr>
						<td class="border-b calc-p-2 calc-pl-8">
							<p>Shear <span>(sec<sup>-1</sup>)</span></p>
						</td>
						<td class="border-b calc-p-2">
							<p>
								{{ shear }}
							</p>
						</td>
					</tr>
					<tr>
						<td class="border-b calc-p-2 calc-pl-8">
							<p>
								Total Surface Area <span>(m<sup>2</sup>)</span>
							</p>
						</td>
						<td v-if="calculateClicked" class="border-b calc-p-2">
							<p>{{ totalSurfaceAreaOutput }}</p>
						</td>
						<td v-else class="border-b calc-p-2">
							<p></p>
						</td>
					</tr>
					<tr>
						<td class="border-b calc-p-2 calc-pl-8">
							<p>Reynolds Number</p>
						</td>
						<td class="border-b calc-p-2">
							<p>
								{{ reynolds }}
							</p>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, numeric, maxLength } from "@vuelidate/validators";
import SearchAutocomplete from "./SearchAutocomplete.vue";
import Loading from "vue-loading-overlay";

export default {
	name: "CalcOne",
	components: {
		SearchAutocomplete,
		Loading,
	},
	props: {
		loaderSettings: Object,
		productData: Array,
		isLoading: Boolean,
		setLoading: {
			type: Function,
			required: true,
		},
	},
	setup() {
		return {
			v$: useVuelidate(),
		};
	},
	data: () => ({
		form: {
			partNumber: "",
			fibDiameterMM: "",
			fiberCount: "",
			flowRate: "",
			fibLength: "",
			fluidViscosity: "",
			fluidDensity: "",
			totalLength: "",
		},
		partNumber: "",
		fibDiameterM: "", // C11=C9/1000
		flowRatePerFiberCubed: "",
		crossSectionalArea: "", // G9=PI()*(C11/2)^2
		flowRateLMin: "",
		flowRatePerFiberLMin: "",
		velocityPerChannel: "", // H9=F9/G9
		volume: "",
		shear: "", // I9=8*H9/C11
		totalSurfaceArea: "", // K9=PI()*C9/1000*J9*D9
		totalSurfaceAreaOutput: 0,
		flowRateFiber: "", // L9=E9*1000/D9
		reynolds: "", // M9=(1000*M11)*H9*(C11)/(M10*0.001)
		valid: false,
		componentKey: 0,
		validNumberFormat: /(-\d+|\d+)(,\d+)*(\.\d+)*/g,
		errored: false,
		calculateClicked: false,
	}),
	watch: {
		isLoading(value) {
			this.setLoading(value);
		},
	},
	methods: {
		getPartNumber(value) {
			this.form.partNumber = value;

			this.autopopulateFields(this.form.partNumber);
		},
		autopopulateFields(partNumber) {
			const validNumberFormat = /(-\d+|\d+)(,\d+)*(\.\d+)*/g;

			this.setLoadingHandler(true);

			if (!partNumber.match(validNumberFormat)) {
				console.error("Not a valid match");
				return;
			}

			if (typeof partNumber === "undefined" || !partNumber.length > 0) {
				console.error("No part number entered");
				return;
			}

			// Loop through productData object and push each product into the selectedProductArr
			for (const product of Object.entries(this.productData)) {
				// If the inputted part number matches the current part number
				if (
					product[1].Part_Number.toLowerCase() ===
					partNumber.toLowerCase()
				) {
					// Assign the following property values
					this.form.fibDiameterMM = parseFloat(
						product[1].Fiber_ID_MM.match(validNumberFormat)
					);
					this.form.fiberCount = parseInt(product[1].Fiber_Count);
					this.form.fibLength = parseFloat(
						product[1].Effective_Length.match(validNumberFormat)
					);
					this.totalSurfaceArea = product[1].SA_CM2;
				}
			}

			this.setLoadingHandler(false);
		},
		showNoMatchErr() {
			console.error("No matches!");
			return;
		},
		submitForm() {
			this.setLoadingHandler(true);
			this.onSubmit();
		},
		onSubmit() {
			this.v$.$validate();

			if (!this.v$.$error) {
				this.isValid();

				this.calculateClicked = true;

				// Convert to number
				const flowRate = this.form.flowRate;
				const fiberCount = this.form.fiberCount;
				const fiberLength = this.form.fibLength;

				// Find Fiber Diameter (M)
				this.fibDiameterM = this.form.fibDiameterMM / 1000;

				// Find Cross-sectional Area
				const crossSectionalAreaCalculation =
					Math.PI * Math.pow(this.fibDiameterM / 2, 2);
				this.crossSectionalArea =
					crossSectionalAreaCalculation.toExponential(3);

				// Find Flow Rate per fiber (m^3/sec/fiber)

				// D10=D8/1000/60
				const flowRatePerFibConversion = flowRate / 1000 / 60;
				const flowRatePerFibCubedCalculation =
					flowRatePerFibConversion / fiberCount;

				this.flowRatePerFiberCubed =
					flowRatePerFibCubedCalculation.toExponential(3);

				// Find Flow Rate Per Fiber (L/min/fiber) -- E10 = D8 / C8
				this.flowRatePerFiberLMin = Number.parseFloat(
					(flowRate * 1000) / fiberCount / 1000
				).toFixed(4);

				// Find Velocity
				const velocityPerChannel =
					flowRatePerFibCubedCalculation /
					crossSectionalAreaCalculation;

				this.velocityPerChannel =
					Number.parseFloat(velocityPerChannel).toFixed(4);

				// Find Shear -- H8=8*G8/B10
				const shearVal = (8 * velocityPerChannel) / this.fibDiameterM;
				this.shear = Math.round(shearVal + Number.EPSILON); // Round to whole number

				// Find Holdup Volume
				let volumeVal =
					Math.PI *
					Math.pow(this.fibDiameterM / 10 / 2, 2) *
					fiberLength *
					fiberCount; // G35=PI()*((D35/10/2)^2)*F35*E35

				volumeVal = Number.parseFloat(volumeVal * 1000).toFixed(4);

				// Calculate Fiber ID Holdup Volume (From Tool 4) -- PI()*((C34/10/2)^2)*E34*D34
				this.volume = volumeVal;

				// Find Total Surface Area (m^2)
				// const totalSurfaceAreaVal =
				//   Math.PI *
				//   this.fibDiameterM *
				//   (fiberLength / 100) *
				//   this.form.fiberCount;

				let totalSurfaceAreaVal = this.totalSurfaceArea;
				let totalSurfaceAreaValFormatted = 0;

				// Check if totalSurfaceArea is a string
				if (typeof this.totalSurfaceArea === "string") {
					// If it is, split the string into an array
					let totalSurfaceAreaString = this.totalSurfaceArea;

					const regex = /([^\s]+)/gi;

					totalSurfaceAreaVal = totalSurfaceAreaString.split(regex);

					// Convert from cm2 to m2
					totalSurfaceAreaValFormatted =
						totalSurfaceAreaVal[1] / 10000;
				} else {
					// Convert from cm2 to m2
					totalSurfaceAreaValFormatted = totalSurfaceAreaVal / 10000;
				}

				this.totalSurfaceAreaOutput = totalSurfaceAreaValFormatted;

				// Find the Reynolds Value
				const flowRateFiberVal =
					this.form.flowRate / this.form.fiberCount; // L9=E9*1000/D9
				this.flowRateFiber =
					Math.round((flowRateFiberVal + Number.EPSILON) * 100) / 100; /// Round to 2 decimal places

				const fluidDensity = this.form.fluidDensity;

				const reynoldsVal =
					(1000 *
						fluidDensity *
						velocityPerChannel *
						this.fibDiameterM) /
					(this.form.fluidViscosity * 0.001); // M9=(1000*M11)*H9*(C11)/(M10*0.001)

				this.reynolds = Math.round(reynoldsVal + Number.EPSILON); // Round to whole number
			}

			this.setLoadingHandler(false);
		},
		resetForm() {
			// Reset validation dirty states
			this.v$.$reset();

			this.calculateClicked = false;

			// Get the initial data
			const initialData = this.$options.data.call(this);

			// Set data to initial data
			Object.assign(this.$data, initialData);

			// Remount component
			this.$nextTick(function () {
				// Force component to rerender.
				this.componentKey += 1;
			});
		},
		isValid() {
			this.valid = true;
		},
		setLoadingHandler(value) {
			this.setLoading(value);
			this.$emit("loading-updated", value);
		},
	},
	validations() {
		return {
			form: {
				searchInput: {
					maxLength: maxLength(14),
				},
				fibDiameterMM: { required, numeric }, // C9
				fiberCount: { required, numeric }, // D9
				flowRate: { required, numeric }, // E9
				fibLength: { required, numeric }, // J9
				fluidViscosity: { required, numeric }, // M10
				fluidDensity: { required, numeric }, // M11
				totalLength: { numeric },
			},
		};
	},
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
